import { useState, useEffect, useReducer, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useDispatch } from "react-redux";
import { setCredentials, setInvitedUserProfileDialogShown, setIsShowingInvitedUserProfileDialog } from "../../redux/authSlice";
import MutipleChoiceQuestion from "../MultipleChoiceQuestion";
import NumericQuestion from "../NumericQuestion";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import styles from './InvitedUserProfileDialog.module.css';

const TARGET_QUESTION_TEXTS = ["type of firm", "total assets managed"];

const reducer = (state, action) => {
    switch (action.type) {
        case ACTION.NEW_QUESTIONS:
            return { ...state, questions: [...action.payload], dataLoaded: true };
        case ACTION.NEW_RESPONSES:
            const surveyQuestionId = action.payload.surveyQuestionId;
            const newQuestions = structuredClone(state.questions);
            for (let question of newQuestions) {
                if (question.surveyQuestionId === surveyQuestionId) {
                    question.responses = [...action.payload.responses];
                }
            }
            return { ...state, questions: newQuestions };
        default:
            console.log(`unhandled action.type: ${action.type}`);
    }
}

const ACTION = {
    NEW_QUESTIONS: "newQuestions",
    NEW_RESPONSES: "newResponses",
}

const InvitedUserProfileDialog = ({ open, onClose, destination, destinationState }) => {

    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const reduxDispatch = useDispatch();

    const [state, dispatch] = useReducer(reducer, {
        questions: [],
        dataLoaded: false
    });

    const [saving, setSaving] = useState(false);

    const [alertStatus, setAlertStatus] = useState({
        open: false,
        severity: null,
        message: null,
    });

    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertStatus(prev => ({ ...prev, open: false }));
    };

    useEffect(() => {

        if (!open) return;

        let isMounted = true;
        const controller = new AbortController();

        const getProfile = async () => {
            try {
                const response = await axiosPrivate.get('/profile', {
                    signal: controller.signal
                });
                if (isMounted) {
                    const filteredQuestions = response.data.questions.filter(q =>
                        TARGET_QUESTION_TEXTS.some(text => q.text.toLowerCase().includes(text))
                    );
                    dispatch({
                        type: ACTION.NEW_QUESTIONS, payload: filteredQuestions
                    });
                }
            } catch (err) {
                if (isMounted) {
                    console.error(err);
                }
            }
        }

        getProfile();

        return () => {
            isMounted = false;
            controller.abort();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveResponse = async (params) => {

        const json = { ...params };

        try {
            const response = await axiosPrivate.post(`/surveyQuestions/${params.surveyQuestionId}`,
                JSON.stringify(json),
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            dispatch({
                type: ACTION.NEW_RESPONSES, payload: { ...response.data }
            });
            setAlertStatus({
                open: true,
                severity: "success",
                message: "Response saved"
            });
        } catch (err) {
            console.log(err);
            let message;
            if (!err?.response) {
                message = 'No Server Response';
            } else {
                message = 'Unable to save response. Please try again.';
            }
            setAlertStatus({
                open: true,
                severity: "error",
                message: message
            });
        }
    }

    const handleContinue = useCallback(async () => {
        setSaving(true);
        try {
            await axiosPrivate.post('/profile/dismissInvitedUserProfileDialog',
                JSON.stringify({}),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );
            reduxDispatch(setCredentials({
                showInvitedUserProfileDialog: false
            }));
            reduxDispatch(setInvitedUserProfileDialogShown());
            reduxDispatch(setIsShowingInvitedUserProfileDialog(false));
            navigate(destination || '/insights', destinationState ? { state: destinationState, replace: true } : { replace: true });
        } catch (err) {
            console.log(err);
            setAlertStatus({
                open: true,
                severity: "error",
                message: "Something went wrong. Please try again."
            });
        } finally {
            setSaving(false);
        }
    }, [axiosPrivate, reduxDispatch, navigate, destination, destinationState]);

    if (!open) return null;

    return (
        <div className="modal dark">
            <div className={styles.dialog}>

                {!state.dataLoaded ? (
                    <div className={styles.loading}>
                        <CircularProgress size={32} />
                        <span>Loading&hellip;</span>
                    </div>
                ) : (
                    <>
                        <p>So we may provide a more personalized scorecard and peer comparison, please answer these two quick questions:</p>
                        <div className={styles.questionsContainer}>
                            {
                                state.questions.map((question, i) => {
                                    switch (question.surveyQuestionType) {
                                        case "multiple choice":
                                            return (
                                                <div key={i} className={styles.questionContainer}>
                                                    <ol className="title">
                                                        <li value={i + 1}>{question.text}</li>
                                                    </ol>
                                                    <MutipleChoiceQuestion question={question} answerChoices={question.answerChoices} responses={question.responses} saveResponse={saveResponse} compact={true} />
                                                </div>
                                            )
                                        case "numeric":
                                            return (
                                                <div key={i} className={styles.questionContainer}>
                                                    <ol className="title">
                                                        <li value={i + 1}>{question.text}</li>
                                                    </ol>
                                                    <NumericQuestion question={question} answerChoices={question.answerChoices} responses={question.responses} saveResponse={saveResponse} compact={true} />
                                                </div>
                                            )
                                        default:
                                            return null;
                                    }
                                })
                            }
                        </div>

                        <button
                            className={styles.continueButton}
                            disabled={saving}
                            onClick={handleContinue}
                        >
                            {saving ? 'Please wait...' : 'Continue'}
                        </button>
                        <button className="close" onClick={handleContinue}></button>
                    </>
                )}

                <Snackbar open={alertStatus.open} autoHideDuration={3000} onClose={handleAlertClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert onClose={handleAlertClose} variant="filled" severity={alertStatus.severity} sx={{ width: '100%' }}>{alertStatus.message}</Alert>
                </Snackbar>
            </div>
        </div>
    );
}

export default InvitedUserProfileDialog;